/**
 * Header AI block merge for the client-side AI resolver. Computes the
 * `preOrderUpdate` portion of the push-batch payload that resolves a
 * pending header block (`pre_order_metadata.ai_suggestions[]`).
 *
 * Unlike flow edits — which ride the modify buffer into `base.flowChanges`
 * so the resolver sees them directly — header edits land via a LIVE
 * `useUpdatePreOrder` mutation (`PreOrderHeader.dispatchHeaderUpdate`) that
 * never touches the modify buffer. So `base.preOrderUpdate` is empty even
 * when the user just changed the comment or m3_comments. The accept path
 * therefore reads the user's CURRENT state off the live `preOrder` row and
 * does a three-way merge (AI `from` / live value / AI `to`): a value the
 * user moved away from the AI's `from` wins over the AI's `to`, so editing
 * a header comment / m3_comments before accepting no longer discards it.
 */

import type { PreOrder } from "@/app/api/types/preOrder";
import type { ModifyBatchInput } from "@stockline/web";
import type { AiResolveContext, AiDecision } from "./index";
import {
  type AiSuggestionBlock,
  HEADER_METADATA_BLOB_FIELD,
  HEADER_METADATA_FIELDS,
  assembleResolvedMetadata,
  headerCamelKeyToSnake,
  isPlainObject,
  jsonEqual,
  resolveBlocks,
} from "./helpers";

type UserPatch = Record<string, unknown> | undefined;

export function mergeHeaderAi(
  ctx: AiResolveContext,
  headerMetadata: Record<string, unknown> | null,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userUpdate: ModifyBatchInput["preOrderUpdate"],
  decision: AiDecision,
  preOrder: PreOrder,
): ModifyBatchInput["preOrderUpdate"] {
  const userPatch = userUpdate as UserPatch;
  if (decision === "decline") {
    return buildDeclineUpdate(ctx, headerMetadata, blocks, pending, userPatch);
  }
  return buildAcceptUpdate(
    ctx,
    headerMetadata,
    blocks,
    pending,
    userPatch,
    preOrder,
  );
}

// ---------- decline branch ----------

function buildDeclineUpdate(
  ctx: AiResolveContext,
  headerMetadata: Record<string, unknown> | null,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userPatch: UserPatch,
): ModifyBatchInput["preOrderUpdate"] {
  const flipped = resolveBlocks(
    blocks,
    pending,
    "declined",
    ctx.userId,
    ctx.resolvedAt,
  );
  const nextMetadata = assembleResolvedMetadata(headerMetadata, [], flipped);
  return {
    ...userPatch,
    preOrderMetadata: nextMetadata,
  } as ModifyBatchInput["preOrderUpdate"];
}

// ---------- accept branch ----------

function buildAcceptUpdate(
  ctx: AiResolveContext,
  headerMetadata: Record<string, unknown> | null,
  blocks: AiSuggestionBlock[],
  pending: AiSuggestionBlock,
  userPatch: UserPatch,
  preOrder: PreOrder,
): ModifyBatchInput["preOrderUpdate"] {
  const blockChanges = pending.type === "modify" ? (pending.changes ?? {}) : {};
  const liveMeta = isPlainObject(headerMetadata) ? headerMetadata : {};

  // Column writes ride `preOrderUpdate` top-level (camelCase, matching the
  // block's `changes` keys). Metadata-field writes (`sourceDocuments`,
  // `customFields`) and metadata blob sub-keys (`m3_comments`, `nb_colis`,
  // …) merge into `pre_order_metadata`. `finalChanges` records the user's
  // overriding values so the resolved block stamps `modified` + `finalTo`.
  const columnWrites: Record<string, unknown> = {};
  const metaFieldWrites: Record<string, unknown> = {};
  const appliedSubKeys: Record<string, unknown> = {};
  const finalChanges: Record<string, unknown> = {};

  for (const [field, change] of Object.entries(blockChanges)) {
    if (field === HEADER_METADATA_BLOB_FIELD) {
      const { applied, overridden } = mergeBlobSubKeys(
        change,
        liveMeta,
        userPatch,
      );
      Object.assign(appliedSubKeys, applied);
      if (Object.keys(overridden).length > 0) {
        finalChanges[HEADER_METADATA_BLOB_FIELD] = overridden;
      }
      continue;
    }
    const isMetaField = HEADER_METADATA_FIELDS.has(field);
    const currentVal = currentHeaderFieldValue({
      field,
      isMetaField,
      userPatch,
      liveMeta,
      preOrder,
      fallback: change.from,
    });
    const decided = decideField(currentVal, change.from, change.to);
    if (isMetaField) metaFieldWrites[field] = decided.value;
    else columnWrites[field] = decided.value;
    if (decided.overridden) finalChanges[field] = decided.value;
  }

  const finalStatus: "accepted" | "modified" =
    Object.keys(finalChanges).length > 0 ? "modified" : "accepted";
  const flipped = resolveBlocks(
    blocks,
    pending,
    finalStatus,
    ctx.userId,
    ctx.resolvedAt,
    finalStatus === "modified" ? finalChanges : undefined,
  );

  // User always wins on conflict: live metadata (already carries the user's
  // live edits) → AI sub-key writes (only for sub-keys the user left at the
  // AI's `from`) → AI metadata-field writes → flipped blocks last.
  const nextMetadata = assembleResolvedMetadata(
    headerMetadata,
    [appliedSubKeys, metaFieldWrites],
    flipped,
  );
  const independentUserFields = pickIndependentUserFields(
    userPatch,
    blockChanges,
  );

  return {
    ...columnWrites,
    ...independentUserFields,
    preOrderMetadata: nextMetadata,
  } as ModifyBatchInput["preOrderUpdate"];
}

// ---------- three-way merge helpers ----------

/**
 * Three-way decision for a single field. `current` is the user's CURRENT
 * persisted value, `from`/`to` the AI block's before/after.
 *  - current === to → user already at the AI value → apply `to` (accepted).
 *  - current === from → user never touched it → apply `to` (accepted).
 *  - otherwise → user moved it elsewhere → keep `current` (overridden).
 */
function decideField(
  current: unknown,
  from: unknown,
  to: unknown,
): { value: unknown; overridden: boolean } {
  if (jsonEqual(current, to)) return { value: to, overridden: false };
  if (jsonEqual(current, from)) return { value: to, overridden: false };
  return { value: current, overridden: true };
}

interface CurrentFieldArgs {
  field: string;
  isMetaField: boolean;
  userPatch: UserPatch;
  liveMeta: Record<string, unknown>;
  preOrder: PreOrder;
  /** Used when the field can't be read off the live row (unmapped column):
   * fall back so `decideField` treats it as untouched and applies the AI's
   * `to` — i.e. preserves the prior behaviour for fields we can't resolve. */
  fallback: unknown;
}

/**
 * Resolve the user's CURRENT value for a header field:
 *  - buffer `userPatch` wins when it carries the field (future-proofs a
 *    header-aware modify buffer; empty today),
 *  - else the live metadata value for a well-known metadata field,
 *  - else the live column value off the `preOrder` row (snake_case),
 *  - else `fallback` (unmapped column → behave as untouched).
 */
function currentHeaderFieldValue(args: CurrentFieldArgs): unknown {
  const { field, isMetaField, userPatch, liveMeta, preOrder, fallback } = args;
  if (userPatch && field in userPatch) return userPatch[field];
  if (isMetaField) return liveMeta[field] ?? null;
  const snake = headerCamelKeyToSnake(field);
  if (snake) {
    return (preOrder as unknown as Record<string, unknown>)[snake] ?? null;
  }
  return fallback ?? null;
}

/**
 * Sub-key three-way merge for the `preOrderMetadata` blob change. The AI
 * stages `{ from, to }` as subset objects of only the metadata sub-keys it
 * touched (e.g. `{ m3_comments: … }`). For each sub-key the AI actually
 * changed, apply the AI's value only when the user's live value still
 * equals the AI's `from`; otherwise keep the user's live value. This keeps
 * a user-edited m3_comments while still landing an AI nb_colis change the
 * user didn't touch — no whole-blob clobber either direction.
 */
function mergeBlobSubKeys(
  change: { from: unknown; to: unknown },
  liveMeta: Record<string, unknown>,
  userPatch: UserPatch,
): { applied: Record<string, unknown>; overridden: Record<string, unknown> } {
  const aiFrom = isPlainObject(change.from) ? change.from : {};
  const aiTo = isPlainObject(change.to) ? change.to : {};
  const userBlob =
    userPatch && isPlainObject(userPatch[HEADER_METADATA_BLOB_FIELD])
      ? (userPatch[HEADER_METADATA_BLOB_FIELD] as Record<string, unknown>)
      : null;
  const applied: Record<string, unknown> = {};
  const overridden: Record<string, unknown> = {};
  const subKeys = new Set([...Object.keys(aiFrom), ...Object.keys(aiTo)]);
  for (const k of subKeys) {
    const fromK = aiFrom[k] ?? null;
    const toK = aiTo[k] ?? null;
    if (jsonEqual(fromK, toK)) continue; // AI no-op on this sub-key
    const currentK =
      userBlob && k in userBlob ? userBlob[k] : (liveMeta[k] ?? null);
    const decided = decideField(currentK, fromK, toK);
    applied[k] = decided.value;
    if (decided.overridden) overridden[k] = decided.value;
  }
  return { applied, overridden };
}

/**
 * User-shipped header keys that don't overlap with the AI block's
 * `changes` (and aren't the metadata blob sentinel). They get spread
 * onto the final column writes so an unrelated `preOrderUpdate` field
 * survives the merge.
 */
function pickIndependentUserFields(
  userPatch: UserPatch,
  blockChanges: Record<string, unknown>,
): Record<string, unknown> {
  if (!userPatch) return {};
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(userPatch)) {
    if (k === HEADER_METADATA_BLOB_FIELD) continue;
    if (k in blockChanges) continue;
    out[k] = userPatch[k];
  }
  return out;
}
